import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { 
  Bot, Smartphone, MessageSquare, GitBranch, Webhook, ScrollText, Cog,
  Settings, QrCode, Power, RefreshCw, Send, Clock, Users, CheckCircle,
  AlertCircle, ExternalLink, Zap, ArrowRight, Wifi, WifiOff, Inbox,
  UserCheck, X, Phone, Loader2
} from 'lucide-react';

// ==================== BOT CONFIG PAGE ====================
function BotConfigPage() {
  const [config, setConfig] = useState({
    botEnabled: true,
    autoReply: true,
    welcomeMessage: 'Olá! Bem-vindo ao Hit Grill. Como posso ajudar?',
    businessHoursOnly: false,
    businessHoursStart: '08:00',
    businessHoursEnd: '22:00',
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cog size={20} />
            Configurações Gerais do Bot
          </CardTitle>
          <CardDescription>
            Configure o comportamento geral do bot de atendimento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">Bot Ativo</p>
              <p className="text-sm text-slate-500">Ativar/desativar o bot completamente</p>
            </div>
            <Button 
              variant={config.botEnabled ? 'default' : 'outline'}
              onClick={() => setConfig(prev => ({ ...prev, botEnabled: !prev.botEnabled }))}
              className={config.botEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              <Power size={16} className="mr-2" />
              {config.botEnabled ? 'Ativo' : 'Inativo'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">Resposta Automática</p>
              <p className="text-sm text-slate-500">Responder automaticamente mensagens</p>
            </div>
            <Button 
              variant={config.autoReply ? 'default' : 'outline'}
              onClick={() => setConfig(prev => ({ ...prev, autoReply: !prev.autoReply }))}
            >
              {config.autoReply ? 'Ativado' : 'Desativado'}
            </Button>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <label className="block font-medium mb-2">Mensagem de Boas-vindas</label>
            <textarea
              value={config.welcomeMessage}
              onChange={(e) => setConfig(prev => ({ ...prev, welcomeMessage: e.target.value }))}
              className="w-full p-3 border rounded-lg resize-none"
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">Apenas Horário Comercial</p>
              <p className="text-sm text-slate-500">Bot responde apenas no horário definido</p>
            </div>
            <Button 
              variant={config.businessHoursOnly ? 'default' : 'outline'}
              onClick={() => setConfig(prev => ({ ...prev, businessHoursOnly: !prev.businessHoursOnly }))}
            >
              {config.businessHoursOnly ? 'Sim' : 'Não'}
            </Button>
          </div>

          {config.businessHoursOnly && (
            <div className="flex gap-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Início</label>
                <input 
                  type="time" 
                  value={config.businessHoursStart}
                  onChange={(e) => setConfig(prev => ({ ...prev, businessHoursStart: e.target.value }))}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Fim</label>
                <input 
                  type="time" 
                  value={config.businessHoursEnd}
                  onChange={(e) => setConfig(prev => ({ ...prev, businessHoursEnd: e.target.value }))}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
          )}

          <Button className="w-full bg-[#FF6B6B] hover:bg-[#FF5252]">
            Salvar Configurações
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== WHATSAPP PAGE (QR Code via Baileys) ====================
function BotWhatsAppPage() {
  const [status, setStatus] = useState({
    connected: false,
    phone_number: null,
    name: null,
    platform: null
  });
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [subTab, setSubTab] = useState('connection');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get auth token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  // Check connection status
  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/status`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setStatus(data);
      return data.connected;
    } catch (err) {
      console.error('Error checking status:', err);
      setStatus({ connected: false, phone_number: null, name: null });
      return false;
    }
  }, [API_URL]);

  // Fetch QR code
  const fetchQR = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/qr`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setQrCode(data.qr);
      return data;
    } catch (err) {
      console.error('Error fetching QR:', err);
      return null;
    }
  }, [API_URL]);

  // Start connection
  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      
      await fetch(`${API_URL}/api/whatsapp/connect`, { 
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      // Start polling for QR and status
      const pollInterval = setInterval(async () => {
        await fetchQR();
        const isConnected = await checkStatus();
        
        if (isConnected) {
          clearInterval(pollInterval);
          setConnecting(false);
          setQrCode(null);
        }
      }, 2000);

      // Stop polling after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setConnecting(false);
      }, 120000);

    } catch (err) {
      console.error('Error connecting:', err);
      setError('Erro ao iniciar conexão. Verifique se o serviço WhatsApp está rodando.');
      setConnecting(false);
    }
  };

  // Disconnect
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await fetch(`${API_URL}/api/whatsapp/disconnect`, { 
        method: 'POST',
        headers: getAuthHeaders()
      });
      setStatus({ connected: false, phone_number: null, name: null });
      setQrCode(null);
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Erro ao desconectar.');
    } finally {
      setLoading(false);
    }
  };

  // Send test message
  const handleSendTest = async () => {
    if (!testPhone || !testMessage) return;
    
    try {
      setSendingTest(true);
      setError(null);
      
      await fetch(`${API_URL}/api/whatsapp/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone_number: testPhone.replace(/\D/g, ''),
          message: testMessage
        })
      });
      
      setTestMessage('');
      toast.success('Mensagem enviada com sucesso!');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Erro ao enviar mensagem.');
    } finally {
      setSendingTest(false);
    }
  };

  // Initial load and polling
  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  return (
    <div className="space-y-6">
      {/* Header Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {status.connected ? (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <Wifi size={16} />
              Conectado
            </span>
          ) : (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
              <WifiOff size={16} />
              Desconectado
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={checkStatus}>
          <RefreshCw size={14} className="mr-2" />
          Atualizar Status
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Sub-tabs for WhatsApp */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setSubTab('connection')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            subTab === 'connection' 
              ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <QrCode size={14} className="inline mr-2" />
          Conexão
        </button>
        <button
          onClick={() => setSubTab('test')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            subTab === 'test' 
              ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Send size={14} className="inline mr-2" />
          Teste
        </button>
        <button
          onClick={() => setSubTab('commands')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            subTab === 'commands' 
              ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500' 
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Bot size={14} className="inline mr-2" />
          Comandos
        </button>
      </div>

      {/* Connection Sub-tab */}
      {subTab === 'connection' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Code Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode size={20} className="text-emerald-600" />
                Conexão via QR Code
              </CardTitle>
              <CardDescription>
                Escaneie o QR Code com seu WhatsApp para conectar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {status.connected ? (
                <div className="text-center space-y-4">
                  <div className="w-24 h-24 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle size={48} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-emerald-700">Conectado!</h3>
                    {status.name && (
                      <p className="text-slate-600">{status.name}</p>
                    )}
                    {status.phone_number && (
                      <p className="text-sm text-slate-500">
                        +{status.phone_number}
                      </p>
                    )}
                  </div>
                  <Button 
                    variant="destructive" 
                    onClick={handleDisconnect}
                    disabled={loading}
                  >
                    {loading ? (
                      <Clock size={16} className="mr-2 animate-spin" />
                    ) : (
                      <Power size={16} className="mr-2" />
                    )}
                    Desconectar
                  </Button>
                </div>
              ) : connecting || qrCode ? (
                <div className="text-center space-y-4">
                  {qrCode ? (
                    <>
                      <div className="bg-white p-4 rounded-lg shadow-inner inline-block border">
                        <img 
                          src={qrCode} 
                          alt="QR Code WhatsApp" 
                          className="w-56 h-56"
                          data-testid="whatsapp-qr-code"
                        />
                      </div>
                      <p className="text-sm text-slate-600">
                        Abra o WhatsApp no seu celular, vá em<br/>
                        <strong>Configurações → Dispositivos conectados → Conectar dispositivo</strong><br/>
                        e escaneie o código acima
                      </p>
                    </>
                  ) : (
                    <div className="py-8">
                      <Clock size={48} className="mx-auto text-emerald-600 animate-spin" />
                      <p className="mt-4 text-slate-600">Gerando QR Code...</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center space-y-4 py-8">
                  <div className="w-24 h-24 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
                    <WifiOff size={48} className="text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-700">WhatsApp Desconectado</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Clique no botão abaixo para gerar o QR Code
                    </p>
                  </div>
                  <Button 
                    onClick={handleConnect}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="whatsapp-connect-btn"
                  >
                    <QrCode size={16} className="mr-2" />
                    Gerar QR Code
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings size={20} />
                Status da Conexão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-600">Status</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    status.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {status.connected ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-600">Número</span>
                  <span className="font-medium">
                    {status.phone_number ? `+${status.phone_number}` : '-'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-slate-600">Nome</span>
                  <span className="font-medium">{status.name || '-'}</span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">Plataforma</span>
                  <span className="font-medium">{status.platform || 'WhatsApp Web'}</span>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <AlertCircle size={16} />
                  Importante
                </h4>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li>• Mantenha seu celular conectado à internet</li>
                  <li>• A sessão permanece ativa mesmo fechando esta página</li>
                  <li>• Desconecte para vincular outro número</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Test Sub-tab */}
      {subTab === 'test' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send size={20} className="text-blue-600" />
              Enviar Mensagem de Teste
            </CardTitle>
            <CardDescription>
              Teste o envio de mensagens pelo WhatsApp conectado
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!status.connected ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 flex items-center gap-2">
                <AlertCircle size={16} />
                Conecte o WhatsApp primeiro para enviar mensagens de teste.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Número do telefone</label>
                  <input
                    type="text"
                    placeholder="5511999999999 (com DDI + DDD)"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full p-3 border rounded-lg"
                    data-testid="test-phone-input"
                  />
                  <p className="text-xs text-slate-500">
                    Digite o número completo com código do país (55 para Brasil)
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Mensagem</label>
                  <input
                    type="text"
                    placeholder="Digite sua mensagem de teste..."
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    className="w-full p-3 border rounded-lg"
                    data-testid="test-message-input"
                  />
                </div>

                <Button 
                  onClick={handleSendTest}
                  disabled={sendingTest || !testPhone || !testMessage}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-test-btn"
                >
                  {sendingTest ? (
                    <Clock size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Send size={16} className="mr-2" />
                  )}
                  Enviar Teste
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commands Sub-tab */}
      {subTab === 'commands' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot size={20} className="text-purple-600" />
              Comandos do Chatbot
            </CardTitle>
            <CardDescription>
              Comandos que os clientes podem usar no WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { cmd: 'cardápio', desc: 'Ver produtos disponíveis', icon: '📋' },
                { cmd: 'horário', desc: 'Horário de funcionamento', icon: '🕐' },
                { cmd: 'delivery', desc: 'Informações sobre entrega', icon: '🚚' },
                { cmd: 'pagamento', desc: 'Formas de pagamento', icon: '💳' },
                { cmd: 'pedido', desc: 'Status do último pedido', icon: '📦' },
                { cmd: 'endereço', desc: 'Localização da loja', icon: '📍' },
                { cmd: 'promoções', desc: 'Ofertas do dia', icon: '🎉' },
                { cmd: 'ajuda', desc: 'Menu de opções', icon: '❓' },
                { cmd: 'atendente', desc: 'Falar com humano', icon: '👨‍💼' },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <p className="font-medium text-slate-900">{item.cmd}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-emerald-50 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-emerald-800">Como funciona?</h4>
              <p className="mt-2 text-sm text-emerald-700">
                Quando um cliente envia uma mensagem para o número conectado, 
                o chatbot identifica a intenção e responde automaticamente com 
                as informações relevantes. Isso agiliza o atendimento e permite 
                que sua equipe foque em casos mais complexos.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== MENSAGENS AUTOMATICAS ====================
function BotMensagensPage() {
  const [messages, setMessages] = useState([
    { id: 1, trigger: 'cardapio', response: 'Nosso cardápio está disponível em: pdv.hitgrill.com.br/menu', active: true },
    { id: 2, trigger: 'horario', response: 'Funcionamos de Terça a Domingo, das 18h às 23h.', active: true },
    { id: 3, trigger: 'endereco', response: 'Estamos na Rua Principal, 123 - Centro', active: true },
    { id: 4, trigger: 'pedido', response: 'Para fazer seu pedido, acesse: pdv.hitgrill.com.br/menu', active: false },
  ]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare size={20} />
              Mensagens Automáticas
            </CardTitle>
            <CardDescription>
              Configure respostas automáticas baseadas em palavras-chave
            </CardDescription>
          </div>
          <Button className="bg-[#FF6B6B] hover:bg-[#FF5252]">
            + Nova Mensagem
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {messages.map((msg) => (
              <div key={msg.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      #{msg.trigger}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      msg.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {msg.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{msg.response}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">Editar</Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setMessages(prev => 
                      prev.map(m => m.id === msg.id ? { ...m, active: !m.active } : m)
                    )}
                  >
                    {msg.active ? 'Desativar' : 'Ativar'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== FLUXOS / AUTOMACOES ====================
function BotFluxosPage() {
  const { token } = useAuth();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFlow, setNewFlow] = useState({ name: '', description: '', trigger_keywords: '', is_active: true });

  useEffect(() => {
    fetchFlows();
  }, [token]);

  const fetchFlows = async () => {
    try {
      const response = await api.get('/api/chat/flows');
      setFlows(response.data.flows || []);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = async () => {
    try {
      const keywords = newFlow.trigger_keywords.split(',').map(k => k.trim()).filter(k => k);
      await api.post('/api/chat/flows', {
        name: newFlow.name,
        description: newFlow.description,
        trigger_keywords: keywords,
        is_active: newFlow.is_active,
        steps: []
      });
      setShowCreateModal(false);
      setNewFlow({ name: '', description: '', trigger_keywords: '', is_active: true });
      fetchFlows();
    } catch (error) {
      console.error('Erro ao criar fluxo:', error);
    }
  };

  const toggleFlowStatus = async (flowId, currentStatus) => {
    try {
      const flow = flows.find(f => f.id === flowId);
      if (flow && !flowId.startsWith('flow_atendimento') && !flowId.startsWith('flow_pedido') && !flowId.startsWith('flow_cardapio')) {
        await api.put(`/api/chat/flows/${flowId}`, {
          ...flow,
          is_active: !currentStatus
        });
        fetchFlows();
      }
    } catch (error) {
      console.error('Erro ao atualizar fluxo:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitBranch size={20} />
              Fluxos de Automação
            </CardTitle>
            <CardDescription>
              Crie fluxos de conversa automatizados para o bot
            </CardDescription>
          </div>
          <Button 
            className="bg-[#FF6B6B] hover:bg-[#FF5252]"
            onClick={() => setShowCreateModal(true)}
          >
            + Novo Fluxo
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {flows.map((flow) => (
              <div key={flow.id} className="p-4 border rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">{flow.name}</h3>
                  <span 
                    className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer ${
                      flow.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                    }`}
                    onClick={() => toggleFlowStatus(flow.id, flow.is_active)}
                  >
                    {flow.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                {flow.description && (
                  <p className="text-sm text-slate-500 mb-2">{flow.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Zap size={14} />
                    {flow.steps?.length || 0} etapas
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowRight size={14} />
                    {flow.trigger_count || 0} disparos
                  </span>
                </div>
                {flow.trigger_keywords?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {flow.trigger_keywords.slice(0, 4).map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-100 text-xs rounded">{kw}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modal de criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px]">
            <h3 className="text-lg font-semibold mb-4">Novo Fluxo</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nome do Fluxo</label>
                <Input 
                  value={newFlow.name}
                  onChange={(e) => setNewFlow({...newFlow, name: e.target.value})}
                  placeholder="Ex: Atendimento VIP"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descrição</label>
                <Input 
                  value={newFlow.description}
                  onChange={(e) => setNewFlow({...newFlow, description: e.target.value})}
                  placeholder="Descrição do fluxo"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Palavras-chave (separadas por vírgula)</label>
                <Input 
                  value={newFlow.trigger_keywords}
                  onChange={(e) => setNewFlow({...newFlow, trigger_keywords: e.target.value})}
                  placeholder="vip, especial, premium"
                />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={newFlow.is_active}
                  onChange={(e) => setNewFlow({...newFlow, is_active: e.target.checked})}
                />
                <label className="text-sm">Ativo</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button onClick={handleCreateFlow} className="bg-[#FF6B6B]">Criar Fluxo</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== WEBHOOKS ====================
function BotWebhooksPage() {
  const { token } = useAuth();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [newWebhook, setNewWebhook] = useState({ name: '', url: '', events: 'message.new', is_active: true });

  useEffect(() => {
    fetchWebhooks();
  }, [token]);

  const fetchWebhooks = async () => {
    try {
      const response = await api.get('/api/chat/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Erro ao carregar webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      const events = newWebhook.events.split(',').map(e => e.trim()).filter(e => e);
      await api.post('/api/chat/webhooks', {
        name: newWebhook.name,
        url: newWebhook.url,
        events: events,
        is_active: newWebhook.is_active
      });
      setShowCreateModal(false);
      setNewWebhook({ name: '', url: '', events: 'message.new', is_active: true });
      fetchWebhooks();
    } catch (error) {
      console.error('Erro ao criar webhook:', error);
    }
  };

  const handleTestWebhook = async (webhookId) => {
    setTestingId(webhookId);
    try {
      const response = await api.post(`/api/chat/webhooks/${webhookId}/test`);
      alert(response.data.success ? 'Webhook testado com sucesso!' : `Erro: ${response.data.error || 'Falha no teste'}`);
    } catch (error) {
      alert('Erro ao testar webhook');
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Tem certeza que deseja excluir este webhook?')) return;
    try {
      await api.delete(`/api/chat/webhooks/${webhookId}`);
      fetchWebhooks();
    } catch (error) {
      console.error('Erro ao excluir webhook:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Webhook size={20} />
              Webhooks
            </CardTitle>
            <CardDescription>
              Configure integrações externas via webhooks
            </CardDescription>
          </div>
          <Button 
            className="bg-[#FF6B6B] hover:bg-[#FF5252]"
            onClick={() => setShowCreateModal(true)}
          >
            + Novo Webhook
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {webhooks.map((wh) => (
              <div key={wh.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium">{wh.name}</p>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      wh.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {wh.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 font-mono">{wh.url}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Eventos: {wh.events?.join(', ') || 'N/A'} | 
                    Sucesso: {wh.success_count || 0} | 
                    Erros: {wh.error_count || 0}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleTestWebhook(wh.id)}
                    disabled={testingId === wh.id}
                  >
                    {testingId === wh.id ? <Loader2 className="animate-spin w-4 h-4" /> : 'Testar'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteWebhook(wh.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Excluir
                  </Button>
                </div>
              </div>
            ))}
            {webhooks.length === 0 && (
              <p className="text-center text-slate-500 py-8">
                Nenhum webhook configurado. Clique em "+ Novo Webhook" para criar.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal de criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px]">
            <h3 className="text-lg font-semibold mb-4">Novo Webhook</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input 
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook({...newWebhook, name: e.target.value})}
                  placeholder="Ex: Integração CRM"
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL do Webhook</label>
                <Input 
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({...newWebhook, url: e.target.value})}
                  placeholder="https://seu-servidor.com/webhook"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Eventos (separados por vírgula)</label>
                <Input 
                  value={newWebhook.events}
                  onChange={(e) => setNewWebhook({...newWebhook, events: e.target.value})}
                  placeholder="message.new, conversation.created"
                />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={newWebhook.is_active}
                  onChange={(e) => setNewWebhook({...newWebhook, is_active: e.target.checked})}
                />
                <label className="text-sm">Ativo</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button onClick={handleCreateWebhook} className="bg-[#FF6B6B]">Criar Webhook</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== LOGS ====================
function BotLogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [token, levelFilter]);

  const fetchLogs = async () => {
    try {
      const params = levelFilter ? `?level=${levelFilter}` : '';
      const response = await api.get(`/api/chat/logs${params}`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Tem certeza que deseja limpar todos os logs?')) return;
    try {
      await api.delete('/api/chat/logs');
      setLogs([]);
    } catch (error) {
      console.error('Erro ao limpar logs:', error);
    }
  };

  const getLevelStyle = (level) => {
    switch (level) {
      case 'ERROR': return 'bg-red-50 text-red-700';
      case 'WARNING': return 'bg-yellow-50 text-yellow-700';
      case 'INFO': return 'bg-blue-50 text-blue-700';
      case 'DEBUG': return 'bg-slate-50 text-slate-700';
      default: return 'bg-slate-50 text-slate-700';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'ERROR': return <AlertCircle size={16} className="text-red-500" />;
      case 'WARNING': return <AlertCircle size={16} className="text-yellow-500" />;
      case 'INFO': return <CheckCircle size={16} className="text-blue-500" />;
      case 'DEBUG': return <CheckCircle size={16} className="text-slate-500" />;
      default: return <CheckCircle size={16} className="text-emerald-500" />;
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ScrollText size={20} />
              Logs do Bot
            </CardTitle>
            <CardDescription>
              Histórico de atividades e eventos do bot
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <select 
              className="border rounded px-3 py-1 text-sm"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
              <option value="DEBUG">Debug</option>
            </select>
            <Button variant="outline" onClick={fetchLogs}>
              <RefreshCw size={16} className="mr-2" />
              Atualizar
            </Button>
            <Button variant="outline" onClick={handleClearLogs} className="text-red-500">
              Limpar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {logs.map((log) => (
              <div key={log.id} className={`flex items-center justify-between p-3 rounded-lg text-sm ${getLevelStyle(log.level)}`}>
                <div className="flex items-center gap-3">
                  {getLevelIcon(log.level)}
                  <div>
                    <span className="font-medium">[{log.level}]</span> {log.message}
                    {log.details && Object.keys(log.details).length > 0 && (
                      <span className="text-xs ml-2 opacity-70">
                        {JSON.stringify(log.details).substring(0, 50)}...
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-mono opacity-70">
                  {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                </span>
              </div>
            ))}
            {logs.length === 0 && (
              <p className="text-center text-slate-500 py-8">
                Nenhum log registrado ainda.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== BOT CONVERSATIONS PAGE (INBOX) ====================
function BotConversasPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState({ status: 'ALL', search: '' });
  const messagesEndRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Função para formatar o telefone/identificador do WhatsApp
  const formatPhoneNumber = (channelUserId) => {
    if (!channelUserId) return 'Desconhecido';
    
    // Remove sufixos do WhatsApp (@c.us, @g.us, @lid, @newsletter, etc.)
    let phone = channelUserId.replace(/@.*$/, '');
    
    // Se for um grupo
    if (channelUserId.includes('@g.us')) {
      return `Grupo ${phone.substring(0, 8)}...`;
    }
    // Se for newsletter/canal
    if (channelUserId.includes('@newsletter')) {
      return `Canal ${phone.substring(0, 8)}...`;
    }
    // Se for linked device
    if (channelUserId.includes('@lid')) {
      // Tenta extrair o número real (formato: número:timestamp@lid)
      const parts = phone.split(':');
      if (parts.length > 0) {
        phone = parts[0];
      }
    }
    
    // Se o número tem mais de 10 dígitos e começa com 55 (Brasil)
    if (phone.length >= 12 && phone.startsWith('55')) {
      const ddd = phone.substring(2, 4);
      const numero = phone.substring(4);
      if (numero.length === 9) {
        return `(${ddd}) ${numero.substring(0, 5)}-${numero.substring(5)}`;
      } else if (numero.length === 8) {
        return `(${ddd}) ${numero.substring(0, 4)}-${numero.substring(4)}`;
      }
    }
    
    // Se não conseguir formatar, retorna o número (truncado se muito grande)
    if (phone.length > 15) {
      return phone.substring(0, 12) + '...';
    }
    return phone;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Carregar conversas
  // Carregar conversas
  const loadConversations = useCallback(async () => {
    console.log('[loadConversations] Carregando lista de conversas...');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[loadConversations] Token não encontrado');
        return;
      }
      
      const url = `${API_URL}/api/chat/conversations`;
      console.log('[loadConversations] URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[loadConversations] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[loadConversations] Conversas carregadas:', data.length);
        setConversations(data);
      } else {
        const errorText = await response.text();
        console.error('[loadConversations] Erro:', response.status, errorText);
      }
    } catch (error) {
      console.error('[loadConversations] Erro de rede:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Carregar mensagens
  const loadMessages = useCallback(async (conversationId) => {
    console.log('[loadMessages] Carregando mensagens para:', conversationId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[loadMessages] Token não encontrado');
        return;
      }
      
      const url = `${API_URL}/api/chat/conversations/${conversationId}/messages`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[loadMessages] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[loadMessages] Mensagens carregadas:', data.length);
        setMessages(data);
        setTimeout(scrollToBottom, 100);
      } else {
        const errorText = await response.text();
        console.error('[loadMessages] Erro:', response.status, errorText);
      }
    } catch (error) {
      console.error('[loadMessages] Erro de rede:', error);
    }
  }, [API_URL]);

  // Selecionar conversa
  const handleSelectConversation = (conv) => {
    setActiveConversation(conv);
    loadMessages(conv.id);
  };

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!activeConversation || !newMessage.trim() || sending) {
      console.log('[handleSendMessage] Condição não atendida:', {
        hasConversation: !!activeConversation,
        hasMessage: !!newMessage.trim(),
        isSending: sending
      });
      return;
    }
    
    console.log('[handleSendMessage] Enviando mensagem para:', activeConversation.id);
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        setSending(false);
        return;
      }
      
      const url = `${API_URL}/api/chat/conversations/${activeConversation.id}/send`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: newMessage.trim() })
      });
      
      console.log('[handleSendMessage] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[handleSendMessage] Success:', data);
        setNewMessage('');
        await loadMessages(activeConversation.id);
        toast.success('Mensagem enviada!');
      } else {
        const errorText = await response.text();
        console.error('[handleSendMessage] Erro:', response.status, errorText);
        toast.error(`Erro ao enviar: ${response.status}`);
      }
    } catch (error) {
      console.error('[handleSendMessage] Erro de rede:', error);
      toast.error('Erro de conexão ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  // Assumir conversa
  const handleAssign = async () => {
    if (!activeConversation) {
      console.log('[handleAssign] Nenhuma conversa selecionada');
      return;
    }
    
    console.log('[handleAssign] Iniciando - conversationId:', activeConversation.id);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }
      
      const url = `${API_URL}/api/chat/conversations/${activeConversation.id}/assign`;
      console.log('[handleAssign] Chamando API:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[handleAssign] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[handleAssign] Response data:', data);
        
        // Atualizar estado local da conversa ativa
        setActiveConversation(prev => ({ 
          ...prev, 
          status: data.status || 'HUMAN_ACTIVE'
        }));
        
        // Recarregar lista de conversas
        await loadConversations();
        toast.success('Conversa assumida com sucesso!');
      } else {
        const errorText = await response.text();
        console.error('[handleAssign] Erro na API:', response.status, errorText);
        toast.error(`Erro ao assumir: ${response.status}`);
      }
    } catch (error) {
      console.error('[handleAssign] Erro de rede:', error);
      toast.error('Erro de conexão ao assumir conversa');
    }
  };

  // Encerrar conversa
  const handleClose = async () => {
    if (!activeConversation) return;
    
    console.log('[handleClose] Fechando conversa:', activeConversation.id);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/chat/conversations/${activeConversation.id}/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[handleClose] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[handleClose] Success:', data);
        setActiveConversation(null);
        await loadConversations();
        toast.success('Conversa encerrada');
      } else {
        const errorText = await response.text();
        console.error('[handleClose] Erro:', response.status, errorText);
        toast.error(`Erro ao encerrar: ${response.status}`);
      }
    } catch (error) {
      console.error('[handleClose] Erro de rede:', error);
      toast.error('Erro ao encerrar conversa');
    }
  };

  // Devolver para bot
  const handleBotResume = async () => {
    if (!activeConversation) return;
    
    console.log('[handleBotResume] Devolvendo para bot:', activeConversation.id);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/chat/conversations/${activeConversation.id}/bot-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('[handleBotResume] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[handleBotResume] Success:', data);
        setActiveConversation(prev => ({ 
          ...prev, 
          status: data.status || 'OPEN'
        }));
        await loadConversations();
        toast.success('Conversa devolvida ao bot');
      } else {
        const errorText = await response.text();
        console.error('[handleBotResume] Erro:', response.status, errorText);
        toast.error(`Erro ao devolver: ${response.status}`);
      }
    } catch (error) {
      console.error('[handleBotResume] Erro de rede:', error);
      toast.error('Erro ao devolver para bot');
    }
  };

  // Aliases para as funções
  const handleTakeOver = handleAssign;
  const handleResumeBot = handleBotResume;
  const handleCloseConversation = handleClose;

  // Carregar conversas inicialmente e polling
  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Recarregar mensagens quando mudar conversa
  useEffect(() => {
    if (activeConversation) {
      const interval = setInterval(() => loadMessages(activeConversation.id), 5000);
      return () => clearInterval(interval);
    }
  }, [activeConversation?.id, loadMessages]);

  // Filtrar conversas
  const filteredConversations = conversations.filter(conv => {
    if (filter.status !== 'ALL' && conv.status !== filter.status) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return (
        conv.channel_user_id?.toLowerCase().includes(search) ||
        conv.customer_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-emerald-100 text-emerald-700',
      WAITING_HUMAN: 'bg-amber-100 text-amber-700',
      HUMAN_ACTIVE: 'bg-blue-100 text-blue-700',
      CLOSED: 'bg-slate-100 text-slate-500'
    };
    const labels = {
      OPEN: 'Bot',
      WAITING_HUMAN: 'Aguardando',
      HUMAN_ACTIVE: 'Humano',
      CLOSED: 'Fechada'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.OPEN}`}>
        {labels[status] || status}
      </span>
    );
  };

  const canSend = activeConversation && (activeConversation.status === 'HUMAN_ACTIVE' || activeConversation.status === 'WAITING_HUMAN');

  return (
    <div className="bg-white rounded-xl border shadow-sm" style={{ height: 'calc(100vh - 240px)', minHeight: '500px' }}>
      <div className="flex h-full">
        {/* Lista de conversas - Coluna Esquerda */}
        <div className="w-72 flex-shrink-0 border-r bg-slate-50 flex flex-col h-full">
          <div className="p-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Users size={16} />
                Conversas
              </h3>
              <Button variant="ghost" size="sm" onClick={loadConversations} disabled={loading}>
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
            
            <input
              type="text"
              placeholder="Buscar..."
              value={filter.search}
              onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border rounded-lg"
            />
            
            <div className="flex gap-1 mt-2 flex-wrap">
              {['ALL', 'OPEN', 'WAITING_HUMAN', 'HUMAN_ACTIVE'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(prev => ({ ...prev, status }))}
                  className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                    filter.status === status
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {status === 'ALL' ? 'Todas' : status === 'OPEN' ? 'Bot' : status === 'WAITING_HUMAN' ? 'Aguardando' : 'Humano'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma conversa</p>
              </div>
            ) : (
              filteredConversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-2.5 rounded-lg cursor-pointer transition-colors ${
                    activeConversation?.id === conv.id
                      ? 'bg-blue-100 border border-blue-200'
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        {conv.customer_name || formatPhoneNumber(conv.channel_user_id)}
                      </p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {conv.last_preview || 'Sem mensagens'}
                      </p>
                    </div>
                    {getStatusBadge(conv.status)}
                  </div>
                  {conv.last_message_at && (
                    <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                      <Clock size={10} />
                      {new Date(conv.last_message_at).toLocaleString('pt-BR', {
                        hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat - Coluna Central */}
        <div className="flex-1 flex flex-col h-full min-w-0">
          {!activeConversation ? (
            <div className="h-full flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto mb-4 opacity-30" />
                <p>Selecione uma conversa</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header do Chat */}
              <div className="p-3 border-b bg-white flex-shrink-0 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm truncate">
                      {activeConversation.customer_name || formatPhoneNumber(activeConversation.channel_user_id)}
                    </h3>
                    <div className="text-xs">{getStatusBadge(activeConversation.status)}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(activeConversation.status === 'OPEN' || activeConversation.status === 'WAITING_HUMAN') && (
                    <Button 
                      size="sm" 
                      onClick={handleAssign} 
                      className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                      data-testid="assign-btn-header"
                    >
                      <CheckCircle size={12} className="mr-1" />
                      Assumir
                    </Button>
                  )}
                  {activeConversation.status === 'HUMAN_ACTIVE' && (
                    <Button size="sm" variant="outline" onClick={handleBotResume} className="text-xs h-8">
                      <Bot size={12} className="mr-1" />
                      Bot
                    </Button>
                  )}
                  {activeConversation.status !== 'CLOSED' && (
                    <Button size="sm" variant="ghost" onClick={handleClose} className="text-red-600 text-xs h-8">
                      <X size={12} />
                    </Button>
                  )}
                </div>
              </div>

              {/* Área de Mensagens */}
              <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
                <div className="space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma mensagem</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'IN' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
                            msg.direction === 'IN'
                              ? 'bg-white text-slate-900 rounded-bl-md border'
                              : 'bg-emerald-500 text-white rounded-br-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.direction === 'IN' ? 'text-slate-400' : 'text-emerald-100'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input de Mensagem */}
              <div className="p-3 border-t bg-white flex-shrink-0">
                {!canSend ? (
                  <div className="bg-amber-50 text-amber-700 p-2.5 rounded-lg text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    {activeConversation.status === 'CLOSED'
                      ? 'Conversa encerrada'
                      : 'Clique em "Assumir" para responder'}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Digite sua mensagem..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      disabled={sending}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      data-testid="message-input"
                    />
                    <Button 
                      onClick={handleSendMessage} 
                      disabled={!newMessage.trim() || sending} 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="send-message-btn"
                    >
                      {sending ? <Clock size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Painel de Detalhes - Coluna Direita */}
        <div className="w-64 flex-shrink-0 border-l bg-slate-50 flex flex-col h-full">
          {!activeConversation ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-4">
              <Users size={28} className="mb-2 opacity-50" />
              <p className="text-xs text-center">Selecione uma conversa</p>
            </div>
          ) : (
            <div className="p-3 space-y-3 overflow-y-auto flex-1">
              <h3 className="font-semibold text-sm">Detalhes</h3>
              
              <div className="p-2 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Status</p>
                {getStatusBadge(activeConversation.status)}
              </div>
              
              <div className="p-2 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Cliente</p>
                <p className="font-medium text-sm">{activeConversation.customer_name || 'Não identificado'}</p>
              </div>
              
              <div className="p-2 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Telefone</p>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Phone size={12} className="text-green-600" />
                  {formatPhoneNumber(activeConversation.channel_user_id)}
                </p>
              </div>
              
              <div className="p-2 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Canal</p>
                <p className="font-medium text-sm">{activeConversation.channel}</p>
              </div>
              
              <div className="pt-2 mt-2 border-t space-y-2">
                <p className="text-xs text-slate-500 font-medium">Ações</p>
                
                {(activeConversation.status === 'OPEN' || activeConversation.status === 'WAITING_HUMAN') && (
                  <Button 
                    onClick={handleTakeOver}
                    className={`w-full text-white text-xs h-8 ${
                      activeConversation.status === 'WAITING_HUMAN' 
                        ? 'bg-amber-500 hover:bg-amber-600' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    data-testid="take-over-btn"
                  >
                    <UserCheck size={14} className="mr-1" />
                    {activeConversation.status === 'WAITING_HUMAN' ? 'Atender' : 'Assumir'}
                  </Button>
                )}
                
                {activeConversation.status === 'HUMAN_ACTIVE' && (
                  <Button 
                    onClick={handleResumeBot}
                    variant="outline"
                    className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-xs h-8"
                    data-testid="resume-bot-btn"
                  >
                    <Bot size={14} className="mr-1" />
                    Devolver ao Bot
                  </Button>
                )}
                
                {activeConversation.status !== 'CLOSED' && (
                  <Button 
                    onClick={handleCloseConversation}
                    variant="ghost"
                    className="w-full text-red-600 hover:bg-red-50 text-xs h-8"
                    data-testid="close-conversation-btn"
                  >
                    <X size={14} className="mr-1" />
                    Encerrar
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN BOT PAGE ====================
export function BotPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determina a aba ativa baseada na URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/bot/conversas')) return 'conversas';
    if (path.includes('/bot/whatsapp')) return 'whatsapp';
    if (path.includes('/bot/mensagens')) return 'mensagens';
    if (path.includes('/bot/fluxos')) return 'fluxos';
    if (path.includes('/bot/webhooks')) return 'webhooks';
    if (path.includes('/bot/logs')) return 'logs';
    return 'config';
  };

  const [activeTab, setActiveTab] = useState(getActiveTab());

  const handleTabChange = (value) => {
    setActiveTab(value);
    // Não navega mais para página separada, tudo fica no BotPage
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Bot size={28} className="text-[#FF6B6B]" />
              Central do Bot
            </h1>
            <p className="text-slate-500 mt-1">
              Gerencie todas as automações e integrações do bot de atendimento
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-4 sm:grid-cols-7 w-full max-w-4xl bg-slate-100 p-1 rounded-lg">
            <TabsTrigger value="config" className="flex items-center gap-1.5 text-xs">
              <Cog size={14} />
              Config
            </TabsTrigger>
            <TabsTrigger value="conversas" className="flex items-center gap-1.5 text-xs">
              <Inbox size={14} />
              Conversas
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-1.5 text-xs">
              <Smartphone size={14} />
              WhatsApp
            </TabsTrigger>
            <TabsTrigger value="mensagens" className="flex items-center gap-1.5 text-xs">
              <MessageSquare size={14} />
              Mensagens
            </TabsTrigger>
            <TabsTrigger value="fluxos" className="flex items-center gap-1.5 text-xs">
              <GitBranch size={14} />
              Fluxos
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex items-center gap-1.5 text-xs">
              <Webhook size={14} />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-1.5 text-xs">
              <ScrollText size={14} />
              Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="mt-6">
            <BotConfigPage />
          </TabsContent>
          <TabsContent value="conversas" className="mt-6">
            <BotConversasPage />
          </TabsContent>
          <TabsContent value="whatsapp" className="mt-6">
            <BotWhatsAppPage />
          </TabsContent>
          <TabsContent value="mensagens" className="mt-6">
            <BotMensagensPage />
          </TabsContent>
          <TabsContent value="fluxos" className="mt-6">
            <BotFluxosPage />
          </TabsContent>
          <TabsContent value="webhooks" className="mt-6">
            <BotWebhooksPage />
          </TabsContent>
          <TabsContent value="logs" className="mt-6">
            <BotLogsPage />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}

export default BotPage;
